import { useEffect, useRef, useCallback, memo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

export interface ConfirmModalProps {
  open: boolean;
  title?: string;
  message?: string;
  messageHtml?: string;
  confirmText?: string;
  cancelText?: string;
  confirmClass?: string;
  confirmDisabled?: boolean;
  busy?: boolean;
  showCancel?: boolean;
  scrollLock?: boolean;
  autoFocus?: 'confirm' | 'cancel' | 'none';
  theme?: string;
  dialogClass?: string;
  className?: string;
  onConfirm?: (() => void) | undefined;
  onCancel: () => void;
  children?: React.ReactNode;
}

export const ConfirmModal = memo(function ConfirmModal({
  open,
  title = 'Confirm Action',
  message = '',
  messageHtml = '',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  confirmClass = 'danger',
  confirmDisabled = false,
  busy = false,
  showCancel = true,
  scrollLock = true,
  autoFocus = 'cancel',
  theme: themeProp,
  dialogClass,
  className,
  onConfirm,
  onCancel,
  children,
}: ConfirmModalProps) {
  const { theme: globalTheme } = useTheme();
  const theme = themeProp ?? globalTheme;
  const dialogRef = useRef<HTMLDivElement>(null);

  // Scroll lock
  useEffect(() => {
    if (!scrollLock) return;
    if (open) {
      document.body.classList.add('kstc-scroll-lock');
    }
    return () => {
      document.body.classList.remove('kstc-scroll-lock');
    };
  }, [open, scrollLock]);

  // Auto-focus
  useEffect(() => {
    if (!open || autoFocus === 'none') return;
    const el = dialogRef.current;
    if (!el) return;
    const selector =
      autoFocus === 'confirm'
        ? `.kstc-btn--${confirmClass}`
        : '.kstc-btn--secondary';
    const button = el.querySelector(selector) as HTMLElement | null;
    button?.focus();
  }, [open, autoFocus, confirmClass]);

  // Escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    },
    [onCancel, busy]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <div data-kstc-modal className={`kstc-backdrop ${theme}${className ? ' ' + className : ''}`}>
      <div className={`kstc-dialog${dialogClass ? ' ' + dialogClass : ''}`} role="dialog" aria-modal="true" ref={dialogRef}>
        <div className="kstc-header">
          <h2 className="kstc-title">{title}</h2>
          <button
            className="kstc-x"
            onClick={onCancel}
            disabled={busy}
            aria-label="Close"
            type="button"
          >
            &times;
          </button>
        </div>

        <div className="kstc-body">
          {message && !messageHtml && <p className="kstc-message">{message}</p>}
          {messageHtml && (
            <p
              className="kstc-message"
              dangerouslySetInnerHTML={{ __html: messageHtml }}
            />
          )}
          {children}
        </div>

        <div className="kstc-actions">
          {showCancel && (
            <button
              className="kstc-btn kstc-btn--secondary"
              onClick={onCancel}
              disabled={busy}
              type="button"
            >
              {cancelText}
            </button>
          )}
          {onConfirm && (
            <button
              className={`kstc-btn kstc-btn--${confirmClass}`}
              disabled={confirmDisabled || busy}
              onClick={onConfirm}
              type="button"
            >
              {confirmText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
});
